import useActiveOrdersContext from '@/contexts/activeOrders/useActiveOrdersContext'
import { getScheduledOrdersKanban } from '@/pages/ScheduledOrders/utils'
import { OrderStatus } from '@/resources/enums'
import { NotificationType } from '@/resources/enums/orders'
import { ActiveOrder } from '@/resources/types'
import { Interval } from '@/resources/types/websocket'
import {
  getNotificationType,
  hasPendingOrders,
  updateScheduledOrdersNotificationTracking,
  validatePendingOrders,
} from '@/resources/utils/orders/notification'
import { CHECK_ORDERS_POLLING_TIME } from '@/seller.application.polling.time.config'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import OrderBell, { OrderRef } from '../OrderBell'

const CheckOrders = () => {
  const { activeOrders, isFromWss, isQueryFetching } = useActiveOrdersContext()
  const bellRef = useRef<OrderRef>(null)
  const pendingOrdersRef = useRef<string[]>([])
  const notificationIntervalRef = useRef<Interval>(null)

  const activeOrdersRef = useRef<ActiveOrder[]>([])
  const acceptedScheduledOrdersRef = useRef<ActiveOrder[]>([])

  const acceptedScheduledOrders = useMemo(
    () =>
      getScheduledOrdersKanban({
        orders: activeOrders,
        includedStatus: [OrderStatus.POC_ACCEPTED, OrderStatus.DM_ACCEPTED],
      }),
    [activeOrders]
  )

  activeOrdersRef.current = activeOrders
  acceptedScheduledOrdersRef.current = acceptedScheduledOrders

  const checkOrdersNotification = useCallback(() => {
    const { hasPending, hasTurbo } = hasPendingOrders(activeOrdersRef.current)

    if (hasPending) {
      bellRef.current?.playAudio(getNotificationType(hasTurbo))
    } else if (acceptedScheduledOrdersRef.current.length > 0) {
      const shouldPlayScheduledAudio =
        updateScheduledOrdersNotificationTracking(
          acceptedScheduledOrdersRef.current
        )
      if (shouldPlayScheduledAudio) {
        bellRef.current?.playAudio(NotificationType.SCHEDULED)
      }
    }
  }, [])

  useEffect(() => {
    notificationIntervalRef.current = setInterval(
      checkOrdersNotification,
      CHECK_ORDERS_POLLING_TIME
    )

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current)
      }
    }
  }, [checkOrdersNotification])

  useEffect(() => {
    if (isFromWss || !isQueryFetching) {
      if (!activeOrders) return

      const { hasNewPendingOrders, hasNewTurboOrders } = validatePendingOrders(
        activeOrders,
        pendingOrdersRef
      )

      if (hasNewPendingOrders) {
        bellRef.current?.playAudio(getNotificationType(hasNewTurboOrders))
      } else if (acceptedScheduledOrders.length > 0) {
        const shouldPlayScheduledAudio =
          updateScheduledOrdersNotificationTracking(acceptedScheduledOrders)
        if (shouldPlayScheduledAudio) {
          bellRef.current?.playAudio(NotificationType.SCHEDULED)
        }
      }
    }
  }, [isQueryFetching, isFromWss, activeOrders, acceptedScheduledOrders])

  return <OrderBell ref={bellRef} />
}

export default CheckOrders
